import { EntityStatusChip } from '../../../components/entity-detail/EntityStatusChip.js';
import type { EntityStatusTone } from '../../../components/entity-detail/EntityStatusChip.js';
import {
  LIST_CREATE_BUTTON_STYLE,
  LIST_ICON_BUTTON_STYLE,
  LIST_SURFACE_STYLE,
  TABLE_SORTER_TOOLTIP,
} from '../../../shared/ui/list-styles.js';
import { QuietEmpty, QuietListSkeleton } from '../../../shared/ui/quiet-states.js';
import { useI18nSubscription } from '../../i18n/useI18nSubscription.js';
import { i18n } from '../../../i18n/index.js';
import { useMemo, useState, type ReactElement } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button, Space, Tooltip, Typography, Table } from 'antd';
import type { ColumnType } from 'antd/es/table/interface';
import { EditOutlined, ReloadOutlined, PlusOutlined } from '@ant-design/icons';

import { PageHeader } from '../../../shared/ui/PageHeader.js';
import { FilterBar, FilterSearch, FilterSelect } from '../../../shared/ui/FilterBar.js';
import { GridColumnsButton } from '../../../shared/grid/GridColumnsButton.js';
import { useGridProfile, type GridColumnMeta } from '../../../shared/grid/useGridProfile.js';
import { useOrderedColumns } from '../../../shared/grid/useOrderedColumns.js';
import { byDate, byNumber, byOrder, byText } from '../../../shared/ui/tableSorters.js';
import { formatDate } from '../../../shared/format/index.js';
import { useDataViewPagination } from '../../../shared/dataview/useDataViewPagination.js';
import { usePublishSelection } from '../../../shared/selection/useSelectionStore.js';
import { EntityHistoryButton } from '../../activity/components/EntityHistoryButton.js';
import { DeleteEntityButton } from '../../../shared/ui/DeleteEntityButton.js';
import type { InheritedLink } from '../../../shared/ui/LinkCell.js';
import { inheritedViaLabel } from '../../../views/catalog.js';
import { DocumentFormModal } from '../components/DocumentFormModal.js';
import { useDocuments } from '../hooks/useDocuments.js';
import { useDeleteDocumentMutation } from '../hooks/useDocumentMutations.js';
import type { DocumentType, DocumentStatus, DocumentListItem } from '../api/documents.client.js';

const { Text } = Typography;

// Порядок для сортировки колонок «Тип» и «Статус»: алфавит здесь бессмыслен —
// «Подписан» не идёт после «Отправлен» по буквам, но идёт по жизненному циклу.
const TYPE_ORDER: readonly DocumentType[] = ['contract', 'invoice', 'act'];
const STATUS_ORDER: readonly DocumentStatus[] = ['draft', 'sent', 'signed', 'cancelled'];

/** Колонки под управлением вида грида; «Действия» всегда на месте. */
const DOCUMENT_GRID_COLUMNS: GridColumnMeta[] = [
  {
    key: 'title',
    get label() {
      return i18n.t('crm.features.columns.title');
    },
  },
  {
    key: 'type',
    get label() {
      return i18n.t('crm.features.columns.type');
    },
  },
  {
    key: 'status',
    get label() {
      return i18n.t('crm.features.columns.status');
    },
  },
  {
    key: 'amount',
    get label() {
      return i18n.t('crm.features.columns.amount');
    },
  },
  {
    key: 'relation',
    get label() {
      return i18n.t('crm.features.columns.client_deal');
    },
  },
  {
    key: 'createdAt',
    get label() {
      return i18n.t('crm.features.columns.created_at');
    },
  },
];

interface DocumentRow {
  id: string;
  title: string;
  type: DocumentType;
  status: DocumentStatus;
  amount: number | string | null;
  currency: string;
  contactName?: string | null;
  dealTitle?: string | null;
  /**
   * Клиент, подтянутый от соседа: у документа своего контакта нет, а у его сделки
   * есть. Фильтр по контакту на сервере находит такой документ, и пустая ячейка
   * читалась бы как расхождение грида с фильтром. Считает наследование сервер.
   */
  contactInherited?: InheritedLink | undefined;
  createdAt: string;
}

export function DocumentsPage(): ReactElement {
  useI18nSubscription();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [activeQuery, setActiveQuery] = useState('');
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editingDocument, setEditingDocument] = useState<DocumentListItem | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [typeFilter, setTypeFilter] = useState<DocumentType | 'all'>('all');
  const [statusFilter, setStatusFilter] = useState<DocumentStatus | 'all'>('all');

  // Список документов сервер отдаёт целиком (без take), поэтому фильтры типа и
  // статуса держим на клиенте — мгновенно и без лишних запросов.
  const documentsQuery = useDocuments();
  const deleteMutation = useDeleteDocumentMutation();
  // Быстрая заметка цепляется к отмеченным документам — как в остальных гридах.
  usePublishSelection(
    'document',
    (documentsQuery.documents ?? [])
      .filter((doc) => selectedIds.includes(doc.id))
      .map((doc) => ({ id: doc.id, label: doc.title })),
  );

  const visibleDocuments = useMemo(() => {
    return (documentsQuery.documents ?? [])
      .filter((doc) => {
        if (typeFilter !== 'all' && doc.type !== typeFilter) return false;
        if (statusFilter !== 'all' && doc.status !== statusFilter) return false;
        if (activeQuery) {
          const query = activeQuery.toLowerCase();
          if (!doc.title.toLowerCase().includes(query)) return false;
        }
        return true;
      })
      .map((doc) => ({
        ...doc,
        contactName: doc.contact?.firstName
          ? `${doc.contact.firstName} ${doc.contact.lastName || ''}`.trim()
          : null,
        dealTitle: doc.deal?.title ?? null,
        contactInherited: doc.inheritedLinks?.contact,
      }));
  }, [documentsQuery.documents, typeFilter, statusFilter, activeQuery]);

  const pagination = useDataViewPagination(visibleDocuments);

  function handleSearch(value: string): void {
    const nextQuery = value.trim();
    setSearchQuery(nextQuery);
    setActiveQuery(nextQuery);
    pagination.resetPage();
  }

  const columnMap: Record<string, ColumnType<DocumentRow>> = {
    title: {
      title: i18n.t('crm.features.features.documents.pages.documentspage.название'),
      dataIndex: 'title',
      key: 'title',
      sorter: byText<DocumentRow>((row) => row.title),
      render: (title: string, record) => (
        <Link
          to={`/documents/${record.id}`}
          style={{ fontWeight: 500, color: 'var(--ant-color-text)' }}
          onClick={(e) => e.stopPropagation()}
        >
          {title}
        </Link>
      ),
    },
    type: {
      title: i18n.t('crm.features.features.documents.pages.documentspage.тип'),
      dataIndex: 'type',
      key: 'type',
      width: 120,
      sorter: byOrder<DocumentRow, DocumentType>((row) => row.type, TYPE_ORDER),
      render: (type: DocumentType) => {
        const labels: Record<DocumentType, string> = {
          contract: i18n.t('crm.features.features.documents.pages.documentspage.договор'),
          invoice: i18n.t('crm.features.features.documents.pages.documentspage.счет'),
          act: i18n.t('crm.features.features.documents.pages.documentspage.акт'),
        };
        return <EntityStatusChip>{labels[type]}</EntityStatusChip>;
      },
    },
    status: {
      title: i18n.t('crm.features.features.documents.pages.documentspage.статус'),
      dataIndex: 'status',
      key: 'status',
      width: 120,
      sorter: byOrder<DocumentRow, DocumentStatus>((row) => row.status, STATUS_ORDER),
      render: (status: DocumentStatus) => {
        const tones: Record<DocumentStatus, EntityStatusTone> = {
          draft: 'default',
          sent: 'processing',
          signed: 'success',
          cancelled: 'default',
        };
        const labels: Record<DocumentStatus, string> = {
          draft: i18n.t('crm.features.features.documents.pages.documentspage.черновик'),
          sent: i18n.t('crm.features.features.documents.pages.documentspage.отправлен'),
          signed: i18n.t('crm.features.features.documents.pages.documentspage.подписан'),
          cancelled: i18n.t('crm.features.features.documents.pages.documentspage.отменен'),
        };
        return <EntityStatusChip tone={tones[status]}>{labels[status]}</EntityStatusChip>;
      },
    },
    amount: {
      title: i18n.t('crm.features.features.documents.pages.documentspage.сумма'),
      dataIndex: 'amount',
      key: 'amount',
      width: 140,
      align: 'right',
      sorter: byNumber<DocumentRow>((row) => row.amount),
      render: (amount: string | null) =>
        amount ? (
          <span className="font-mono font-medium text-gray-700">
            {new Intl.NumberFormat('ru-RU', { style: 'currency', currency: 'RUB' }).format(
              Number(amount),
            )}
          </span>
        ) : (
          <Text disabled>—</Text>
        ),
    },
    relation: {
      title: i18n.t('crm.features.features.documents.pages.documentspage.клиент_сделка'),
      key: 'relation',
      // В ячейке две строки; сортируем по первой — по клиенту, включая
      // унаследованного: иначе такие строки сортируются как пустые, а рисуются с
      // именем.
      sorter: byText<DocumentRow>((row) => row.contactName ?? row.contactInherited?.label ?? null),
      render: (_, record) => (
        <div className="flex flex-col">
          {record.contactName !== null && record.contactName !== undefined ? (
            <Text style={{ fontSize: 13 }}>{record.contactName}</Text>
          ) : record.contactInherited !== undefined ? (
            // Приглушённо и с подсказкой, которая называет звено, — как ячейка
            // представления и остальные гриды.
            <Tooltip
              title={i18n.t('crm.features.dynamic.features.documents.pages.documentspage.65', {
                p0: inheritedViaLabel(record.contactInherited.via),
              })}
            >
              <Text style={{ fontSize: 13 }} type="secondary">
                {record.contactInherited.label}
              </Text>
            </Tooltip>
          ) : (
            <Text disabled>—</Text>
          )}
          <Text type="secondary" style={{ fontSize: 12 }}>
            {record.dealTitle ?? ''}
          </Text>
        </div>
      ),
    },
    createdAt: {
      title: i18n.t('crm.features.features.documents.pages.documentspage.дата_создания'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 120,
      align: 'right',
      sorter: byDate<DocumentRow>((row) => row.createdAt),
      render: (date: string) => (
        <Text type="secondary" style={{ fontSize: 13 }}>
          {formatDate(date)}
        </Text>
      ),
    },
  };

  const actionsColumn: ColumnType<DocumentRow> = {
    title: i18n.t('crm.features.features.documents.pages.documentspage.действия'),
    key: 'action',
    width: 160,
    align: 'center',
    render: (_, record) => (
      <Space size="small">
        <Tooltip
          title={i18n.t('crm.features.features.documents.pages.documentspage.редактировать')}
        >
          <Button
            size="small"
            type="text"
            aria-label={i18n.t('crm.features.dynamic.features.documents.pages.documentspage.66', {
              p0: record.title,
            })}
            icon={<EditOutlined />}
            onClick={(event) => {
              event.stopPropagation();
              const source = (documentsQuery.documents ?? []).find((doc) => doc.id === record.id);
              if (source) setEditingDocument(source);
            }}
          />
        </Tooltip>
        <EntityHistoryButton
          entity="document"
          entityId={record.id}
          title={i18n.t('crm.features.features.documents.pages.documentspage.история_документа')}
          subject={record.title}
          iconOnly
        />
        {/* Клик по строке открывает карточку — гасим его на кнопке удаления. */}
        <span
          onClick={(event) => event.stopPropagation()}
          role="presentation"
          className="inline-flex"
        >
          <DeleteEntityButton
            iconOnly
            label={i18n.t('crm.features.features.documents.pages.documentspage.удалить_документ')}
            title={i18n.t('crm.features.features.documents.pages.documentspage.удалить_документ_2')}
            description={i18n.t(
              'crm.features.features.documents.pages.documentspage.документ_пропадет_из_списков_связанные_сде',
            )}
            loading={deleteMutation.isPending && deletingId === record.id}
            onConfirm={() => {
              setDeletingId(record.id);
              return deleteMutation.mutateAsync(record.id);
            }}
          />
        </span>
      </Space>
    ),
  };

  const grid = useGridProfile('documents', DOCUMENT_GRID_COLUMNS);
  const ordered = useOrderedColumns<DocumentRow>({
    profile: grid,
    columnMap,
    trailing: [actionsColumn],
  });

  return (
    <div className="animate-fade-in flex h-full flex-col">
      <PageHeader
        title={i18n.t('crm.features.features.documents.pages.documentspage.документы')}
        subtitle={
          <>
            <span data-testid="documents-visible-count">{visibleDocuments.length}</span>{' '}
            {i18n.tc('crm.counts.documents', visibleDocuments.length)}
          </>
        }
        actions={
          <div className="flex items-center gap-2">
            <GridColumnsButton profile={grid} />
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setIsCreateOpen(true)}
              style={LIST_CREATE_BUTTON_STYLE}
            >
              {i18n.t('crm.features.features.documents.pages.documentspage.создать_документ')}
            </Button>
            <Tooltip title={i18n.t('crm.features.features.documents.pages.documentspage.обновить')}>
              <Button
                icon={<ReloadOutlined />}
                onClick={() => void documentsQuery.refetch()}
                style={LIST_ICON_BUTTON_STYLE}
              />
            </Tooltip>
          </div>
        }
      />

      <div className="flex min-h-0 flex-1 flex-col gap-3">
        <FilterBar
          search={
            <FilterSearch
              id="documents-search"
              aria-label={i18n.t(
                'crm.features.features.documents.pages.documentspage.поиск_по_документам',
              )}
              placeholder={i18n.t(
                'crm.features.features.documents.pages.documentspage.поиск_по_названию',
              )}
              value={searchQuery}
              onChange={(value) => {
                setSearchQuery(value);
                setActiveQuery(value.trim());
                pagination.resetPage();
              }}
              onPressEnter={handleSearch}
            />
          }
        >
          <FilterSelect
            aria-label={i18n.t(
              'crm.features.features.documents.pages.documentspage.фильтр_по_типу',
            )}
            value={typeFilter}
            onChange={(value) => {
              setTypeFilter(value as DocumentType | 'all');
              pagination.resetPage();
            }}
            options={[
              {
                value: 'all',
                label: i18n.t('crm.features.features.documents.pages.documentspage.все_типы'),
              },
              {
                value: 'contract',
                label: i18n.t('crm.features.features.documents.pages.documentspage.договор_2'),
              },
              {
                value: 'invoice',
                label: i18n.t('crm.features.features.documents.pages.documentspage.счет_2'),
              },
              {
                value: 'act',
                label: i18n.t('crm.features.features.documents.pages.documentspage.акт_2'),
              },
            ]}
          />

          <FilterSelect
            aria-label={i18n.t(
              'crm.features.features.documents.pages.documentspage.фильтр_по_статусу',
            )}
            value={statusFilter}
            onChange={(value) => {
              setStatusFilter(value as DocumentStatus | 'all');
              pagination.resetPage();
            }}
            options={[
              {
                value: 'all',
                label: i18n.t('crm.features.features.documents.pages.documentspage.все_статусы'),
              },
              {
                value: 'draft',
                label: i18n.t('crm.features.features.documents.pages.documentspage.черновик_2'),
              },
              {
                value: 'sent',
                label: i18n.t('crm.features.features.documents.pages.documentspage.отправлен_2'),
              },
              {
                value: 'signed',
                label: i18n.t('crm.features.features.documents.pages.documentspage.подписан_2'),
              },
              {
                value: 'cancelled',
                label: i18n.t('crm.features.features.documents.pages.documentspage.отменен_2'),
              },
            ]}
          />
        </FilterBar>

        <div className="min-h-0 flex-1 crm-checkbox-square" style={LIST_SURFACE_STYLE}>
          {documentsQuery.isLoading ? (
            <QuietListSkeleton rows={6} />
          ) : (
            <Table<DocumentRow>
              showSorterTooltip={TABLE_SORTER_TOOLTIP}
              dataSource={visibleDocuments}
              columns={ordered.columns}
              components={ordered.components}
              rowKey="id"
              size="small"
              rowSelection={{
                selectedRowKeys: selectedIds,
                onChange: (keys) => setSelectedIds(keys.map(String)),
              }}
              pagination={pagination.pagination}
              locale={{
                emptyText: (
                  <QuietEmpty
                    text={i18n.t(
                      'crm.features.features.documents.pages.documentspage.нет_документов',
                    )}
                  />
                ),
              }}
              onRow={(record) => ({
                style: { cursor: 'pointer' },
                onClick: () => navigate(`/documents/${record.id}`),
              })}
            />
          )}
        </div>
      </div>

      {isCreateOpen ? (
        <DocumentFormModal
          open
          onClose={() => setIsCreateOpen(false)}
          onCreated={(documentId) => navigate(`/documents/${documentId}`)}
        />
      ) : null}
      {editingDocument !== null ? (
        <DocumentFormModal
          open
          document={editingDocument}
          onClose={() => setEditingDocument(null)}
        />
      ) : null}
    </div>
  );
}
